import { Variants } from '@rc-d2c/shared-ui/RcButton';
import { RcPopin, RcPopinContent } from '@rc-d2c/shared-ui/RcPopin';
import React, { useCallback, useState } from 'react';

export function usePopin<T = undefined>({
  title,
  description,
  children,
  cancelLabel = 'Annuler',
  confirmLabel = 'Confirmer',
  confirmDisabled,
  onCancel,
  onConfirm,
  customClasses,
  closeButtonVisibility,
  cancelButtonVariant,
  isFullScreenOnMobile,
}: {
  title?: string;
  description?: string;
  children?: (metadata: T | undefined) => React.ReactNode;
  cancelLabel?: string;
  confirmLabel?: string;
  confirmDisabled?: boolean;
  customClasses?: string;
  onCancel?: (metadata: T | undefined) => void;
  onConfirm?: (metadata: T | undefined) => void;
  closeButtonVisibility?: 'always' | 'mobile-only' | 'none';
  cancelButtonVariant?: `${Variants}`;
  isFullScreenOnMobile?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [metadata, setMetadata] = useState<T | undefined>(undefined);

  const toggleIsOpen = useCallback(
    (metadata?: T) => {
      if (!isOpen && metadata) {
        setMetadata(metadata);
      } else {
        setMetadata(undefined);
      }

      setIsOpen(!isOpen);
    },
    [isOpen],
  );

  const handleClickCancel = useCallback(() => {
    if (onCancel) {
      onCancel(metadata);
    }

    setMetadata(undefined);
    setIsOpen(false);
  }, [onCancel, metadata]);

  const handleClickConfirm = useCallback(() => {
    if (onConfirm) {
      onConfirm(metadata);
    }

    setMetadata(undefined);
    setIsOpen(false);
  }, [onConfirm, metadata]);

  const renderModal = useCallback(() => {
    return isOpen ? (
      <RcPopin open={isOpen} onOpenChange={handleClickCancel}>
        <RcPopinContent
          title={title}
          description={description}
          completeButtonLabel={confirmLabel}
          cancelButtonLabel={cancelLabel}
          onClickComplete={onConfirm ? handleClickConfirm : undefined}
          onClickCancel={onConfirm ? handleClickCancel : undefined}
          customClasses={customClasses}
          closeButtonVisibility={closeButtonVisibility}
          isCompleteButtonDisabled={confirmDisabled}
          cancelButtonVariant={cancelButtonVariant}
          isFullScreenOnMobile={isFullScreenOnMobile}
        >
          {children?.(metadata)}
        </RcPopinContent>
      </RcPopin>
    ) : null;
  }, [metadata, isOpen, title, description, children, cancelLabel, confirmLabel, confirmDisabled, handleClickCancel, handleClickConfirm]);

  return {
    toggle: toggleIsOpen,
    render: renderModal,
  };
}

export default usePopin;
